import { useTerminology } from '@/lib/useTerminology'
/**
 * Aktuelle Tätigkeiten — wer ist gerade eingestempelt und woran arbeitet er?
 *
 * Bewusst eine **eigene Seite** und kein Filter in der Zusammenfassung: Das hier
 * ist eine Momentaufnahme, die im Minutentakt veraltet, sich selbst aktualisiert
 * und die laufende Dauer zeigt — die gibt es in der Auswertung nicht, dort wird
 * die Dauer erst beim Ausstempeln berechnet. Die Zusammenfassung beantwortet die
 * verwandte, aber andere Frage „welche Buchungen sind offen geblieben?" über
 * ihren Filter *Läuft gerade*; korrigiert wird dort, nicht hier.
 *
 * Zwei Feinheiten, die leicht falsch gemacht werden:
 *
 * 1. **Die laufende Dauer rechnet mit `startedAt`, nicht mit `startTime`.**
 *    `startTime` ist die abgelesene Uhrzeit, als UTC gespeichert (Wall-Clock-
 *    Konvention, siehe `.claude/rules/zeit-und-datum.md`); eine Differenz zu
 *    `Date.now()` wäre um den Zeitzonen-Offset falsch. `startedAt` ist der echte
 *    Zeitstempel des Einstempelns und damit der richtige Bezug — dieselbe
 *    Rechnung wie im laufenden Zähler der Stempeluhr.
 * 2. **Die Schwelle „läuft auffällig lang" kommt vom Server.** Sie ist die
 *    Mandanten-Einstellung der Verdachtsprüfung und wird mit der Antwort
 *    geliefert; `GET /time-config` wäre ADMIN-only und lieferte einem MANAGER
 *    still einen 403 (KA-03, KA-08).
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Settings, Check, RefreshCw, ArrowUp, ArrowDown, ArrowUpDown, AlertTriangle, Users,
} from 'lucide-react'
import { fmtTime, fmtTimeUTC } from '@/lib/format-date'
import { HelpPopover } from '@/components/ui/HelpPopover'
import { HILFE_VERWEISE } from '@/lib/hilfe'
import { LayoutDefaultActions } from '@/components/ui/LayoutDefaultActions'
import { loadUiSetting, markUiSettingApplied, saveUiSettingIfChanged } from '@/lib/ui-settings'
import { FilterBar } from '@/components/list/FilterBar'
import { timeEntriesApi } from '../api/time-tracking.api'
import type {
  TimeEntry,
} from '../types/time-tracking.types';
import type { FilterFieldDef, ActiveFilter, ColumnFieldDef, ColumnLayout, ColumnSortConfig } from '@/components/list/list.types';
import { pageWide } from '@/lib/form-styles'

// ─── Spaltenlayout (AP-23: Cache + Server) ────────────────────────────────────

const COLUMN_LAYOUT_KEY = 'columnbar_layout_current'

// 'employee' ist die feste erste Spalte und steht deshalb nicht in der Liste.
const DEFAULT_COLUMN_KEYS = ['project', 'activityType', 'since', 'runningFor', 'betriebsstaette']

/**
 * Vorbelegung der FilterBar: **kein Datum** — die Liste zeigt ausschließlich das
 * Jetzt (Regel in `docs/pattern-auswertungsmaske.md`).
 */
const DEFAULT_FILTER_FIELD_KEYS = ['employee', 'project']

/** Wie oft die Liste sich selbst neu holt. */
const REFRESH_MS = 30_000

const SORTABLE_KEYS = new Set([
  'employee', 'project', 'activityType', 'since', 'runningFor', 'betriebsstaette', 'note',
])

const COLUMN_CATEGORY_LABELS: Record<string, string> = {
  core: 'Kerndaten',
  project: 'Projektdaten',
  time: 'Zeitdaten',
}

function loadColumnLayout(): ColumnLayout {
  const parsed = loadUiSetting<ColumnLayout | null>(COLUMN_LAYOUT_KEY, null)
  const columnKeys = (parsed?.columnKeys ?? []).filter((k) => k !== 'employee')
  if (columnKeys.length > 0) {
    return { columnKeys, columnWidths: parsed?.columnWidths ?? {}, sortConfig: parsed?.sortConfig ?? null }
  }
  return { columnKeys: DEFAULT_COLUMN_KEYS, columnWidths: {}, sortConfig: null }
}

function saveColumnLayout(layout: ColumnLayout) {
  saveUiSettingIfChanged(COLUMN_LAYOUT_KEY, layout)
}

// ─── Zeilen-Helfer ────────────────────────────────────────────────────────────

function employeeName(e: TimeEntry): string {
  const p = e.user?.profile
  const name = [p?.firstName, p?.lastName].filter(Boolean).join(' ')
  return name || e.user?.email || '–'
}

function projectName(e: TimeEntry): string {
  // Ref-Layer (AP-09) hat Vorrang, dann die Legacy-Relation, zuletzt der
  // Freitext-Schnappschuss in standardFields.
  const sf = e.standardFields as Record<string, unknown> | null
  return (
    e.projectRef?.name ??
    e.project?.name ??
    (typeof sf?.['projekt'] === 'string' ? (sf['projekt'] as string) : '') ??
    ''
  ) || '–'
}

function activityName(e: TimeEntry): string {
  const sf = e.standardFields as Record<string, unknown> | null
  return (
    e.activityRef?.name ??
    e.activityType?.name ??
    (typeof sf?.['taetigkeit'] === 'string' ? (sf['taetigkeit'] as string) : '') ??
    ''
  ) || '–'
}

function betriebsstaette(e: TimeEntry): string {
  const sf = e.standardFields as Record<string, unknown> | null
  const v = sf?.['betriebsstaette']
  return typeof v === 'string' && v ? v : '–'
}

/**
 * Wie lange läuft die Buchung schon (in Minuten)?
 *
 * Bezug ist `startedAt` (echter Zeitstempel des Einstempelns). Fehlt er — bei
 * Buchungen aus der Zeit vor dem Feld —, bleibt die Spalte leer, statt eine um
 * den Zeitzonen-Offset verschobene Zahl zu zeigen.
 */
function runningMinutes(e: TimeEntry, now: number): number | null {
  if (!e.startedAt) return null
  const started = new Date(e.startedAt).getTime()
  if (Number.isNaN(started)) return null
  return Math.max(0, Math.floor((now - started) / 60_000))
}

function formatRunning(minutes: number | null): string {
  if (minutes == null) return '–'
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return h > 0 ? `${h} h ${String(m).padStart(2, '0')} min` : `${m} min`
}

// ─── Seite ────────────────────────────────────────────────────────────────────

export default function CurrentActivityPage() {
  // AP-16: Begriff des Mandanten (Projekt / Auftrag)
  const terms = useTerminology()
  const { t } = useTranslation()

  const [rows, setRows] = useState<TimeEntry[]>([])
  const [employees, setEmployees] = useState(0)
  const [longRunningHours, setLongRunningHours] = useState(14)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null)

  // Tickt mit, damit „läuft seit" weiterläuft, ohne auf die nächste Abfrage zu warten.
  const [now, setNow] = useState(() => Date.now())

  const [filterValues, setFilterValues] = useState<Record<string, ActiveFilter>>({})
  const [columnLayout, setColumnLayout] = useState<ColumnLayout>(loadColumnLayout)
  const [columnConfigMode, setColumnConfigMode] = useState(false)
  const [addColDropdownOpen, setAddColDropdownOpen] = useState(false)
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)

  const addColRef = useRef<HTMLDivElement>(null)
  const draggedKeyRef = useRef<string | null>(null)
  const resizeRef = useRef<{ key: string; startX: number; startWidth: number } | null>(null)

  const columnWidths = columnLayout.columnWidths ?? {}
  const sortConfig = columnLayout.sortConfig ?? null

  // ── Laden ───────────────────────────────────────────────────────────────────

  const fetchRows = useCallback(async () => {
    try {
      const { data } = await timeEntriesApi.getCurrent()
      setRows(data.data)
      setEmployees(data.meta.employees)
      setLongRunningHours(data.meta.longRunningHours)
      setLastUpdate(new Date())
      setError(null)
    } catch {
      setError('Die laufenden Buchungen konnten nicht geladen werden.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void fetchRows() }, [fetchRows])

  // Selbstaktualisierung — pausiert, solange der Tab im Hintergrund liegt:
  // Eine Momentaufnahme, die niemand ansieht, muss auch nicht geholt werden.
  useEffect(() => {
    const tick = setInterval(() => {
      setNow(Date.now())
      if (document.visibilityState === 'visible') void fetchRows()
    }, REFRESH_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible') { setNow(Date.now()); void fetchRows() }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(tick)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [fetchRows])

  // ── Spalten & Filterfelder ──────────────────────────────────────────────────

  const spaltenKategorien: Record<string, string> = { ...COLUMN_CATEGORY_LABELS, project: terms.data }

  const availableColumns: ColumnFieldDef[] = useMemo(() => ([
    { key: 'project',         label: terms.project,     category: 'project' },
    { key: 'activityType',    label: 'Tätigkeit',       category: 'project' },
    { key: 'since',           label: 'Seit',            category: 'time' },
    { key: 'runningFor',      label: 'Läuft seit',      category: 'time', align: 'right' },
    { key: 'betriebsstaette', label: 'Betriebsstätte',  category: 'project' },
    { key: 'note',            label: 'Notiz',           category: 'core' },
  ]), [terms.project])

  // Die Auswahl speist sich aus den laufenden Buchungen selbst — wer nicht
  // eingestempelt ist, taucht in dieser Liste ohnehin nicht auf.
  const filterFields: FilterFieldDef[] = useMemo(() => {
    const employeesInList = Array.from(
      new Map(rows.map((r) => [r.userId, employeeName(r)])).entries(),
    ).map(([value, label]) => ({ value, label }))

    return [
      {
        key: 'employee', label: 'Mitarbeiter', type: 'select-multi', category: 'core',
        options: employeesInList, operators: ['is', 'is_not'],
      },
      { key: 'project', label: terms.project, type: 'text', category: 'project', operators: ['contains', 'not_contains'] },
      { key: 'activityType', label: 'Tätigkeit', type: 'text', category: 'project', operators: ['contains', 'not_contains'] },
      { key: 'betriebsstaette', label: 'Betriebsstätte', type: 'text', category: 'project', operators: ['contains', 'not_contains'] },
      { key: 'runningHours', label: 'Läuft seit (Stunden)', type: 'number', category: 'core', operators: ['gt', 'lt'] },
    ]
  }, [rows, terms.project])

  // ── Filterung (clientseitig — die Liste ist von Natur aus kurz) ─────────────

  const filteredRows = useMemo(() => rows.filter((e) => {
    for (const [key, active] of Object.entries(filterValues)) {
      if (!active.value) continue
      if (key === 'employee') {
        const ids = active.value.split(',').filter(Boolean)
        if (ids.length === 0) continue
        const hit = ids.includes(e.userId)
        if (active.operator === 'is_not' ? hit : !hit) return false
        continue
      }
      if (key === 'runningHours') {
        const mins = runningMinutes(e, now)
        const limit = parseFloat(active.value.replace(',', '.'))
        if (mins == null || !Number.isFinite(limit)) return false
        if (active.operator === 'gt' && !(mins / 60 > limit)) return false
        if (active.operator === 'lt' && !(mins / 60 < limit)) return false
        continue
      }
      const cell = (
        key === 'project' ? projectName(e)
        : key === 'activityType' ? activityName(e)
        : key === 'betriebsstaette' ? betriebsstaette(e)
        : ''
      ).toLowerCase()
      const val = active.value.toLowerCase()
      if (active.operator === 'contains' && !cell.includes(val)) return false
      if (active.operator === 'not_contains' && cell.includes(val)) return false
    }
    return true
  }), [rows, filterValues, now])

  const cellText = useCallback((e: TimeEntry, key: string): string => {
    switch (key) {
      case 'employee':        return employeeName(e)
      case 'project':         return projectName(e)
      case 'activityType':    return activityName(e)
      case 'since':           return fmtTimeUTC(e.startTime)
      case 'runningFor':      return formatRunning(runningMinutes(e, now))
      case 'betriebsstaette': return betriebsstaette(e)
      case 'note':            return e.note ?? '–'
      default:                return ''
    }
  }, [now])

  const displayRows = useMemo(() => {
    if (!sortConfig) return filteredRows
    const { key, dir } = sortConfig
    const factor = dir === 'asc' ? 1 : -1
    return [...filteredRows].sort((a, b) => {
      if (key === 'runningFor' || key === 'since') {
        // Beides ordnet nach dem Start — bei „Seit" ist das die abgelesene
        // Uhrzeit, bei „Läuft seit" derselbe Zeitpunkt aus anderer Blickrichtung.
        return (new Date(a.startTime).getTime() - new Date(b.startTime).getTime()) * factor
      }
      return cellText(a, key).localeCompare(cellText(b, key), 'de', { sensitivity: 'base' }) * factor
    })
  }, [filteredRows, sortConfig, cellText])

  // ── Spaltenlayout persistieren ──────────────────────────────────────────────
  // AP-23: `…IfChanged` — der Effekt läuft auch beim Mount, und das bloße Öffnen
  // der Liste darf keine eigene Einstellung anlegen.
  useEffect(() => { saveColumnLayout(columnLayout) }, [columnLayout])

  useEffect(() => {
    if (!addColDropdownOpen) return
    const handler = (ev: MouseEvent) => {
      if (addColRef.current && !addColRef.current.contains(ev.target as Node)) setAddColDropdownOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [addColDropdownOpen])

  useEffect(() => {
    const onMove = (ev: MouseEvent) => {
      if (!resizeRef.current) return
      const delta = ev.clientX - resizeRef.current.startX
      const newWidth = Math.max(60, resizeRef.current.startWidth + delta)
      setColumnLayout((prev) => ({
        ...prev,
        columnWidths: { ...(prev.columnWidths ?? {}), [resizeRef.current!.key]: newWidth },
      }))
    }
    const onUp = () => { resizeRef.current = null; document.body.style.cursor = '' }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
  }, [])

  // ── Spalten-Helfer ──────────────────────────────────────────────────────────

  const shownColumns = columnLayout.columnKeys
    .map((k) => availableColumns.find((c) => c.key === k))
    .filter(Boolean) as ColumnFieldDef[]

  const availableToAddColumns = availableColumns.filter((c) => !columnLayout.columnKeys.includes(c.key))

  const groupedAvailableColumns = availableToAddColumns.reduce<Record<string, ColumnFieldDef[]>>((acc, c) => {
    if (!acc[c.category]) acc[c.category] = []
    acc[c.category].push(c)
    return acc
  }, {})

  const handleAddColumn = (key: string) => {
    setColumnLayout((prev) => ({ ...prev, columnKeys: [...prev.columnKeys, key] }))
    setAddColDropdownOpen(false)
  }
  const handleRemoveColumn = (key: string) => {
    setColumnLayout((prev) => ({ ...prev, columnKeys: prev.columnKeys.filter((k) => k !== key) }))
  }

  const handleDragStart = (key: string) => { draggedKeyRef.current = key }
  const handleDragOver = (ev: React.DragEvent, key: string) => {
    ev.preventDefault()
    if (draggedKeyRef.current && draggedKeyRef.current !== key) setDragOverKey(key)
  }
  const handleDrop = (targetKey: string) => {
    const srcKey = draggedKeyRef.current
    if (!srcKey || srcKey === targetKey) { setDragOverKey(null); return }
    setColumnLayout((prev) => {
      const keys = [...prev.columnKeys]
      const from = keys.indexOf(srcKey)
      const to = keys.indexOf(targetKey)
      if (from < 0 || to < 0) return prev
      keys.splice(from, 1)
      keys.splice(to, 0, srcKey)
      return { ...prev, columnKeys: keys }
    })
    draggedKeyRef.current = null
    setDragOverKey(null)
  }
  const handleDragEnd = () => { draggedKeyRef.current = null; setDragOverKey(null) }

  const handleSort = (key: string) => {
    if (!SORTABLE_KEYS.has(key)) return
    setColumnLayout((prev) => {
      const current = prev.sortConfig
      let next: ColumnSortConfig | null
      if (!current || current.key !== key) next = { key, dir: 'asc' }
      else if (current.dir === 'asc') next = { key, dir: 'desc' }
      else next = null
      return { ...prev, sortConfig: next }
    })
  }

  const handleResizeStart = (ev: React.MouseEvent, key: string) => {
    ev.preventDefault()
    ev.stopPropagation()
    const th = (ev.target as HTMLElement).closest('th')
    const startWidth = th ? th.getBoundingClientRect().width : (columnWidths[key] ?? 120)
    resizeRef.current = { key, startX: ev.clientX, startWidth }
    document.body.style.cursor = 'col-resize'
  }

  const sortIcon = (key: string) => {
    if (!SORTABLE_KEYS.has(key)) return null
    if (sortConfig?.key !== key) return <ArrowUpDown className="h-3 w-3 opacity-30" />
    return sortConfig.dir === 'asc'
      ? <ArrowUp className="h-3 w-3 text-primary" />
      : <ArrowDown className="h-3 w-3 text-primary" />
  }

  const totalColCount = 1 + shownColumns.length + (columnConfigMode && availableToAddColumns.length > 0 ? 1 : 0)
  const longRunningMinutes = longRunningHours * 60

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className={pageWide}>
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {t('timeTracking.currentActivity', { defaultValue: 'Aktuelle Tätigkeiten' })}
          </h1>
          <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
            <Users className="h-4 w-4" />
            {t('timeTracking.currentActivityCount', {
              running: rows.length,
              employees,
              defaultValue: '{{running}} von {{employees}} Mitarbeitern eingestempelt',
            })}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {lastUpdate && (
            <span className="text-xs text-muted-foreground">
              {/* `fmtTime`, nicht `fmtTimeUTC`: Der Abrufzeitpunkt ist ein echter
                  Instant, keine abgelesene Buchungszeit. */}
              {t('timeTracking.lastUpdated', { defaultValue: 'Stand' })} {fmtTime(lastUpdate)}
            </span>
          )}
          <button
            type="button"
            onClick={() => { setNow(Date.now()); void fetchRows() }}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
            title={t('timeTracking.refreshNow', { defaultValue: 'Jetzt aktualisieren' })}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            {t('common.refresh', { defaultValue: 'Aktualisieren' })}
          </button>
        </div>
      </div>

      <FilterBar
        availableFields={filterFields}
        value={filterValues}
        onChange={setFilterValues}
        layoutKey="current"
        defaultFieldKeys={DEFAULT_FILTER_FIELD_KEYS}
      />

      <div className="relative rounded-lg border border-border bg-card shadow-sm overflow-hidden">
        <button type="button" onClick={() => setColumnConfigMode((v) => !v)}
          className={`absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md transition-colors ${
            columnConfigMode ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
          title={columnConfigMode ? 'Fertig' : 'Spalten anpassen'}>
          {columnConfigMode ? <Check size={14} /> : <Settings size={14} />}
        </button>

        <HelpPopover topic="columns" className="absolute right-8 top-1.5" hilfe={HILFE_VERWEISE.aktuelleTaetigkeiten} />

        {/* AP-23 Phase D: Zurücksetzen / als Mandanten-Standard speichern */}
        {columnConfigMode && (
          <div className="border-b border-border bg-muted/30 px-3 py-2 pr-16">
            <LayoutDefaultActions
              settingKey={COLUMN_LAYOUT_KEY}
              currentValue={columnLayout}
              onReset={(value) => {
                const next = (value as ColumnLayout | null) ?? {
                  columnKeys: DEFAULT_COLUMN_KEYS, columnWidths: {}, sortConfig: null,
                }
                markUiSettingApplied(COLUMN_LAYOUT_KEY, next)
                setColumnLayout(next)
              }}
            />
          </div>
        )}

        {error && (
          <div className="border-b border-border bg-red-50 px-4 py-3 text-sm text-red-600 dark:bg-red-950 dark:text-red-400">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                {/* Feste erste Spalte: Mitarbeiter — die Frage lautet „wer" */}
                <th className="relative px-3 py-1.5 text-left font-medium text-foreground"
                    style={columnWidths['employee'] ? { width: columnWidths['employee'] } : undefined}>
                  <button type="button" onClick={() => handleSort('employee')} className="flex items-center gap-1 hover:text-primary">
                    Mitarbeiter {sortIcon('employee')}
                  </button>
                  <div className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary"
                       onMouseDown={(e) => handleResizeStart(e, 'employee')} />
                </th>

                {shownColumns.map((col) => (
                  <th key={col.key}
                    className={`relative select-none px-3 py-1.5 font-medium text-foreground
                      ${col.align === 'right' ? 'text-right' : 'text-left'}
                      ${columnConfigMode ? 'cursor-grab' : ''}
                      ${dragOverKey === col.key ? 'bg-primary/10 outline outline-2 outline-primary' : ''}`}
                    draggable={columnConfigMode}
                    onDragStart={columnConfigMode ? () => handleDragStart(col.key) : undefined}
                    onDragOver={columnConfigMode ? (e) => handleDragOver(e, col.key) : undefined}
                    onDrop={columnConfigMode ? () => handleDrop(col.key) : undefined}
                    onDragEnd={columnConfigMode ? handleDragEnd : undefined}
                    style={columnWidths[col.key] ? { width: columnWidths[col.key] } : undefined}>
                    <div className={`flex items-center gap-1 ${col.align === 'right' ? 'justify-end' : ''}`}>
                      {columnConfigMode ? (
                        <>
                          <span>{col.label}</span>
                          <button type="button" onClick={() => handleRemoveColumn(col.key)}
                            className="rounded p-0.5 text-destructive hover:text-destructive/80" title="Entfernen">✕</button>
                        </>
                      ) : (
                        <button type="button" onClick={() => handleSort(col.key)}
                          className={`flex items-center gap-1 ${SORTABLE_KEYS.has(col.key) ? 'hover:text-primary' : 'cursor-default'}`}>
                          {col.label} {sortIcon(col.key)}
                        </button>
                      )}
                    </div>
                    {!columnConfigMode && (
                      <div className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary"
                           onMouseDown={(e) => handleResizeStart(e, col.key)} />
                    )}
                  </th>
                ))}

                {columnConfigMode && availableToAddColumns.length > 0 && (
                  <th className="px-2 py-1.5">
                    <div className="relative" ref={addColRef}>
                      <button type="button" onClick={() => setAddColDropdownOpen((v) => !v)}
                        className="rounded-md border border-dashed border-border px-2 py-1 text-xs text-muted-foreground hover:text-foreground">
                        + Spalte
                      </button>
                      {addColDropdownOpen && (
                        <div className="absolute right-0 top-8 z-50 max-h-72 min-w-[200px] overflow-y-auto rounded-md border border-border bg-background shadow-lg">
                          {Object.entries(groupedAvailableColumns).map(([cat, cols]) => (
                            <div key={cat}>
                              <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                {spaltenKategorien[cat] ?? cat}
                              </div>
                              {cols.map((c) => (
                                <button key={c.key} type="button" onClick={() => handleAddColumn(c.key)}
                                  className="flex w-full items-center px-4 py-2 text-sm text-foreground hover:bg-muted">
                                  {c.label}
                                </button>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </th>
                )}
              </tr>
            </thead>

            <tbody>
              {loading && displayRows.length === 0 && (
                <tr><td colSpan={totalColCount} className="px-6 py-8 text-center text-muted-foreground">Laden…</td></tr>
              )}

              {!loading && displayRows.length === 0 && (
                <tr>
                  <td colSpan={totalColCount} className="px-6 py-8 text-center text-sm text-muted-foreground">
                    {rows.length === 0
                      ? 'Gerade ist niemand eingestempelt.'
                      : 'Keine Buchung passt zu den gesetzten Filtern.'}
                  </td>
                </tr>
              )}

              {displayRows.map((e, idx) => {
                const mins = runningMinutes(e, now)
                const longRunning = mins != null && mins > longRunningMinutes
                return (
                  <tr key={e.id}
                    className={`border-b border-border last:border-0 transition-colors hover:bg-accent/30 ${
                      idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'
                    }`}>
                    <td className="whitespace-nowrap px-3 py-1.5 font-medium text-foreground">
                      {employeeName(e)}
                    </td>
                    {shownColumns.map((col) => (
                      <td key={col.key}
                        className={`whitespace-nowrap px-3 py-1.5 ${
                          col.align === 'right' ? 'text-right tabular-nums' : ''
                        } ${col.key === 'runningFor' && longRunning ? 'font-medium text-amber-600 dark:text-amber-500' : 'text-muted-foreground'}`}>
                        {col.key === 'runningFor' && longRunning ? (
                          <span className="inline-flex items-center gap-1"
                                title={`Läuft länger als ${longRunningHours} Stunden — vermutlich vergessen auszustempeln`}>
                            <AlertTriangle className="h-3.5 w-3.5" />
                            {cellText(e, col.key)}
                          </span>
                        ) : (
                          cellText(e, col.key)
                        )}
                      </td>
                    ))}
                    {columnConfigMode && availableToAddColumns.length > 0 && <td />}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
